import { useMemo } from 'react';
import { GOALS_MAP } from '@/widgets/Goals';
import { useUserStore } from '@/entities/User';
import { useModalStore } from '@/entities/Modal';
import { Typography } from '@/shared/ui/Typography';
import { SingleChart } from '@/features/SingleChart';
import { classNames } from '@/shared/lib/classNames';
import { useGlobalStore } from '@/entities/GlobalStore';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { DepositButton } from '../../DepositButton';
import ArrowRight from '@/shared/assets/icons/arrowRight.svg?react';
import cls from './ChosenGoal.module.scss';

const getCalendarOffset = (width: number): [number, number] => {
    if (width > 1199) {
        return [-250, 0];
    }

    if (width > 1023) {
        return [-250, 0];
    }

    if (width > 767) {
        return [-100, 0];
    }

    return [-20, 0];
};

export const ChosenGoal = () => {
    const {
        user: { percentLeftToAchieve, goalMeters, goalRooms },
    } = useUserStore();
    const { setSelectedGoalParams } = useGlobalStore();
    const { setIsGoalModalOpen } = useModalStore();

    const SelectedRoom = GOALS_MAP.get(goalRooms)!.schemeImageSVG;

    const pieChartData = useMemo(() => {
        const filledValue = 100 - percentLeftToAchieve;

        return [
            { name: 'notFilled', value: percentLeftToAchieve },
            { name: 'filled', value: filledValue > 100 ? 100 : filledValue },
        ];
    }, [percentLeftToAchieve]);

    const width = useResize();

    const calendarOffset = getCalendarOffset(width);

    const isFirstBlurShown = percentLeftToAchieve <= 90;
    const isSecondBlurShown = percentLeftToAchieve <= 80;

    return (
        <article className={cls.container}>
            <div className={classNames(cls.card, {}, [cls.data])}>
                <div className={cls.heading}>
                    <Typography className={cls.title} as="h6" variant="h6_bold">
                        Моя цель
                    </Typography>
                    <button
                        className={cls.button}
                        type="button"
                        onClick={() => {
                            setSelectedGoalParams({ initialRoomsCount: goalRooms, initialSquareMeters: goalMeters });
                            setIsGoalModalOpen(true);
                        }}
                    >
                        <Typography as="span" variant="text_16_green">
                            Изменить цель
                        </Typography>
                        <ArrowRight />
                    </button>
                </div>
                <div className={cls.info}>
                    <div className={cls.image}>
                        <SelectedRoom width="100%" height="100%" />
                        <div
                            className={cls.filledPercentBackground}
                            style={{ height: `calc(${100 - percentLeftToAchieve}% - 28px)` }}
                        >
                            <div className={cls.blurs}>
                                <div className={classNames(cls.firstBlur, { [cls.hidden]: !isFirstBlurShown })}></div>
                                <div className={classNames(cls.secondBlur, { [cls.hidden]: !isSecondBlurShown })}></div>
                            </div>
                        </div>
                    </div>
                    <div className={cls.params}>
                        <div className={cls.param}>
                            <Typography as="p" variant="text_16">
                                Количество комнат:
                            </Typography>
                            <Typography as="p" variant="h6_green">
                                {goalRooms}
                            </Typography>
                        </div>
                        <div className={cls.param}>
                            <Typography as="p" variant="text_16">
                                Количество метров:
                            </Typography>
                            <Typography as="p" variant="h6_green">
                                {goalMeters} м²
                            </Typography>
                        </div>
                        <div className={cls.param}>
                            <Typography as="p" variant="text_16">
                                Осталось накопить:
                            </Typography>
                            <div className={cls.pieChartContainer}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <defs>
                                            <linearGradient x1="0" y1="0" x2="1" y2="1" id="pieGradient">
                                                <stop offset="0%" stopColor="#15B015" />
                                                <stop offset="100%" stopColor="#04C6C9" />
                                            </linearGradient>
                                        </defs>
                                        <Pie
                                            cx="50%"
                                            cy="50%"
                                            dataKey="value"
                                            innerRadius="70%"
                                            outerRadius="100%"
                                            data={pieChartData}
                                        >
                                            {pieChartData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.name === 'filled' ? 'url(#pieGradient)' : '#D5DFE6'}
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <Typography className={cls.percents} as="p" variant="text_16">
                                    {percentLeftToAchieve}%
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classNames(cls.card, {}, [cls.chart])}>
                <Typography className={cls.title} as="h6" variant="h6_bold">
                    Динамика стоимости юнита
                </Typography>
                <SingleChart
                    squareClassName={cls.square}
                    legendClassName={cls.legend}
                    chartTicksClassName={cls.chartTicks}
                    lastCouponClassName={cls.lastCoupon}
                    chartControlsClassName={cls.chartControls}
                    chartContainerClassName={cls.chartContainer}
                    className={classNames('LKSIngleChart', {}, [cls.chartCN])}
                    yAxisOffset={10}
                    chartOffset={-20}
                    chartControlsSize="s"
                    calendarOffset={calendarOffset}
                />
                <DepositButton />
            </div>
        </article>
    );
};
